import { useEffect, useRef, useState } from "react";
import usePrevious from "src/hooks/usePrevious/usePrevious";
import { useExperiment } from "src/modules/Experiments";
import { ProductWithAmount } from "src/redux/modals/types";
import { addDays } from "src/utils/date/addDays";
import { getStartOfDay } from "src/utils/date/getStartOfDay";
import { sendViewProductEvent } from "src/utils/marketing/enhancedEcommerce";
import calculateTimeLeft from "./utils/calculateTimeLeft";

const formatValue = (value: number) => {
  return value.toString().padStart(2, '0');
};

const useDiscountProps = (product: ProductWithAmount) => {
  const { discount, ean } = product;
  const prevEan = usePrevious(ean);

  const discountVariant = useExperiment('discount');

  const date = discount.due_date ? new Date(discount.due_date) : new Date();
  const targetDate = getStartOfDay(addDays(date, 1));

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  const hours = timeLeft.days > 0 ? (24 * timeLeft.days) + timeLeft.hours : timeLeft.hours;
  const timer = `${formatValue(hours)}:${formatValue(timeLeft.minutes)}:${formatValue(timeLeft.seconds)}`;
  const discountValue = discount && discount.status && discount.value ? discount.value : 0;

  const timerRef = useRef(timer);
  const isInitialMount = useRef(true);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [targetDate, discountVariant]);

  useEffect(() => {
    if (isInitialMount.current || prevEan !== product.ean) {
      sendViewProductEvent(product);
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [product, prevEan]);

  const limit = 24 * 4;
  const resultTimer = hours <= limit ? timer : '';

  return {
    timer: discountValue > 0 ? resultTimer : '',
    discountValue,
    discountVariant,
  };
};

export default useDiscountProps;
